/**
 * Core Layer - Transcription Manager
 * Handles Azure Speech SDK integration for transcription and translation
 * No state management, no business logic - pure transcription operations
 */

import {
    AudioConfig,
    AutoDetectSourceLanguageConfig,
    SpeechRecognizer,
    SpeechTranslationConfig,
    TranslationRecognizer
} from "microsoft-cognitiveservices-speech-sdk";

export class TranscriptionManager {
    constructor() {
        this.recognizer = null;
        this.audioConfig = null;
    }

    /**
     * Create audio config from default microphone
     */
    createAudioConfig() {
        this.audioConfig = AudioConfig.fromDefaultMicrophoneInput();
        return this.audioConfig;
    }

    /**
     * Create speech translation config
     */
    createSpeechTranslationConfig(token, region, sourceLanguage, targetLanguages = []) {
        const config = SpeechTranslationConfig.fromAuthorizationToken(token, region);
        config.speechRecognitionLanguage = sourceLanguage;

        // Add target languages for translation
        targetLanguages.forEach(language => config.addTargetLanguage(language));

        return config;
    }

    /**
     * Create translation recognizer
     */
    createTranslationRecognizer(token, region, sourceLanguage, targetLanguages = []) {
        if (!this.audioConfig) {
            this.createAudioConfig();
        }

        const speechConfig = this.createSpeechTranslationConfig(token, region, sourceLanguage, targetLanguages);
        this.recognizer = new TranslationRecognizer(speechConfig, this.audioConfig);

        return this.recognizer;
    }

    /**
     * Set recognized event handler
     */
    onRecognized(handler) {
        if (this.recognizer) {
            this.recognizer.recognized = handler;
        }
    }

    /**
     * Start continuous recognition
     */
    startContinuousRecognition(onSuccess, onError) {
        if (this.recognizer) {
            this.recognizer.startContinuousRecognitionAsync(onSuccess, onError);
        }
    }

    /**
     * Stop continuous recognition
     */
    stopContinuousRecognition() {
        if (this.recognizer) {
            this.recognizer.stopContinuousRecognitionAsync();
        }
    }

    /**
     * Close and cleanup recognizer
     */
    close() {
        return new Promise((resolve) => {
            if (this.recognizer) {
                this.recognizer.close(resolve);
                this.recognizer = null;
            } else {
                resolve();
            }
        });
    }

    /**
     * Update authorization token (for token refresh)
     */
    updateToken(token) {
        if (this.recognizer) {
            this.recognizer.authorizationToken = token;
        }
    }

    /**
     * Detect language from audio
     */
    async detectLanguage(token, region, candidateLanguages) {
        if (!this.audioConfig) {
            this.createAudioConfig();
        }

        const speechConfig = SpeechTranslationConfig.fromAuthorizationToken(token, region);
        speechConfig.speechRecognitionLanguage = 'en-US';
        speechConfig.setProperty('SpeechServiceConnection_LanguageIdMode', "Continuous");

        const autoDetectConfig = AutoDetectSourceLanguageConfig.fromLanguages(candidateLanguages);
        const speechRecognizer = SpeechRecognizer.FromConfig(speechConfig, autoDetectConfig, this.audioConfig);

        return new Promise((resolve, reject) => {
            speechRecognizer.recognized = (sender, event) => {
                const {result} = event;
                speechRecognizer.stopContinuousRecognitionAsync();
                speechRecognizer.close();

                if (result.errorDetails) {
                    reject(result.errorDetails);
                    return;
                }

                resolve(result.language);
            };

            speechRecognizer.startContinuousRecognitionAsync();
        });
    }

    /**
     * Get recognizer instance
     */
    getRecognizer() {
        return this.recognizer;
    }
}
